import { isValidSchemaType } from '@rsos/base-utils/hooks/useAdditionalData';
import { validatedPayload } from '@rsos/ellington';
import { parseSchema } from '@rsos/ellington/src/Renderer';
import { HIDDEN } from '@rsos/ellington/src/formats';
import buildSchemaMismatchReport from '@rsos/ellington/src/helpers/buildSchemaMismatchReport';
import findData from '@rsos/ellington/src/helpers/findData';
import formatData, {
  NO_DATA_DEFAULT,
} from '@rsos/ellington/src/helpers/formatData';
import {
  OBJECT,
  STRING,
  ARRAY,
  INTEGER,
  NUMBER,
  BOOLEAN,
  NULL,
} from '@rsos/ellington/src/types';
import isBlank from '@rsos/ellington/src/utils/isBlank';

/**
 * Creates a string of just \t. \t is the tab
 * @param {Number} tabLevel - how many \t should be added
 * @returns string of \t equivalent ot tabLevel
 */
const getTabString = (tabLevel = 0) => {
  let tabs = '';
  for (let i = 0; i < tabLevel; i++) {
    tabs += '\t';
  }
  return tabs;
};

class SchemaTypeMismatchError extends Error {
  constructor(type = '') {
    super(
      `Unrecognized type ${type} in the provided JSON Schema. Unable to convert to string.`,
    );
    this.name = 'SchemaTypeMismatchError';
  }
}

class DataToSchemaTypeMismatchError extends Error {
  constructor(type = '') {
    super(
      `Type ${type} does not match the provided display schema. Unable to convert to string.`,
    );
    this.name = 'DataToSchemaTypeMismatchError';
  }
}

/**
 * Mimicking @rsos/ellington's walkSchema,
 * returns a large string instead of components
 * @param {Object} schema - The JSON Schema
 * @param {*} path - The schema path to start recursing on. By default,
 * empty indicated the root
 * @param {Object} payloadData - The Actual JSON Data
 * @param {Boolean} shouldDisplayBlankFields - Show default value if field is blank
 * @param {Function} onError - Function to add onto additional error handling
 * @param {returnString} returnString - string that gets modified during recursion
 * @param {Number} tabLevel - determines how far down in recursion a string is pushed
 * @param {Object} context - `{ displaySchema, ownProps }` used to enrich
 * SchemaTypeMismatchError reports with the card's root schema/provider info
 */
export const walkSchema = (
  schema,
  path = [],
  payloadData,
  shouldDisplayBlankFields,
  onError = () => {},
  returnString = '',
  tabLevel = 0,
  context = {},
) => {
  const { propertiesOrder, properties } = schema;
  let sortKeys = propertiesOrder || [];

  if (!sortKeys.length) {
    if (schema instanceof Array) {
      sortKeys = schema;
    } else {
      sortKeys = [schema];
    }
  }

  const fullResult = sortKeys
    .map(key => {
      // NOTE if the key is a string, it's a propertiesOrder key. Otherwise,
      // the key is likely to be a subset of the entire schema.

      const isPropertiesOrderKey = typeof key === 'string';

      let property = key;
      if (isPropertiesOrderKey) {
        // An attempt to fail gracefully if the given key is not found within
        // the listed properties. If that's the case, null is returned so React
        // knows to ignore it as a rendered component.
        if (!properties[key]) {
          return null;
        }
        property = properties[key];
      } else if (JSON.stringify(property) === '{}') {
        return null;
      }
      const { type } = property;

      const configPath = [...path, isPropertiesOrderKey ? key : null].filter(
        v => v !== null,
      );
      if (!configPath.length) {
        return null;
      }

      const config = {
        schema: property,
        data: payloadData,
        path: configPath,
        shouldDisplayBlankFields,
        onError,
        returnString,
        tabLevel,
      };

      if (property.format && property.format === HIDDEN) {
        return null;
      }

      if (type === OBJECT) {
        const tabs = getTabString(tabLevel);
        const objectTitle = config.schema.title ? config.schema.title : 'N/A';
        const objectString = `${tabs}${objectTitle}\n`;

        const result = walkSchema(
          property,
          config.path,
          payloadData,
          shouldDisplayBlankFields,
          onError,
          objectString,
          tabLevel + 1,
          context,
        );

        if (!result.length) {
          return null;
        }

        return objectString + result;
      } else if (type === ARRAY) {
        // According to spec, items can be an object for single type elements
        // or an array for multi-type elements. This implementation assumes that
        // items are defined as an object literal and that all items in the
        // array are of the same type.
        const { items } = property;
        const tabs = getTabString(tabLevel);
        const raw = findData(config.data, config.path);

        if (!raw) {
          // Data is optional, so if not found, return an empty component.
          return null;
        }

        if (!(raw instanceof Array)) {
          const { extra, fingerprint, tags } = buildSchemaMismatchReport({
            type,
            property,
            path: config.path,
            displaySchema: context.displaySchema,
            ownProps: context.ownProps,
            data: raw,
            errorType: 'DataToSchemaTypeMismatch',
            fingerprintPrefix: 'ellington-data-schema-type-mismatch',
          });
          onError(
            new DataToSchemaTypeMismatchError(type),
            extra,
            fingerprint,
            tags,
          );
          return null;
        }

        const arrayTitle = config.schema.title ? config.schema.title : 'N/A';
        let arrayString = `${tabs}${arrayTitle}\n`;

        raw.map((item, idx) => {
          // This gives an extra space to make it clearer different part of an array
          if (idx > 0) {
            arrayString += '\n';
          }
          const result = walkSchema(
            items,
            [...config.path, idx],
            payloadData,
            shouldDisplayBlankFields,
            onError,
            arrayString,
            tabLevel + 1,
            context,
          );
          arrayString += result;
        });

        if (arrayString.lastIndexOf('\n') !== arrayString.length - 1) {
          arrayString += '\n';
        }

        return arrayString;
      } else if (
        type === STRING ||
        type === INTEGER ||
        type === NUMBER ||
        type === BOOLEAN ||
        type === NULL
      ) {
        // Purposefully ignoring Custom Components
        return handleData(config);
      }

      // If the schema type isn't supported throw an error.
      const { extra, fingerprint, tags } = buildSchemaMismatchReport({
        type,
        property,
        path: config.path,
        displaySchema: context.displaySchema,
        ownProps: context.ownProps,
        data: findData(config.data, config.path),
      });
      onError(new SchemaTypeMismatchError(type), extra, fingerprint, tags);
    })
    .filter(child => {
      // filter out null
      if (child) {
        return child;
      }
    });

  let resultString = '';
  fullResult.map(stringedData => {
    resultString += stringedData;
  });

  return resultString;
};

/**
 * Similar to @rsos/ellington's createComponent but instead of returning a component, returns the data
 * @param {Object} config - object of schema, payloadData, path, onError, shouldDisplayBlankFields, and tabLevel
 * @returns data as string
 */
export function handleData(config) {
  const {
    schema,
    data: payloadData,
    path,
    onError,
    shouldDisplayBlankFields = false,
    tabLevel,
  } = config;
  let text;

  try {
    let data = findData(payloadData, path);

    if (!shouldDisplayBlankFields && isBlank(data)) {
      return null;
    }

    if (!data && schema.default) {
      data = schema.default;
    } else if (!data && data !== 0 && !schema.default) {
      // don't render the field at all.
      return null;
    }

    const formatted = formatData(schema, data);

    // formatData returns a component, data is always either the direct child as text
    // or in src of child component
    text = formatted.props.children || formatted.props.src;
  } catch (error) {
    text = NO_DATA_DEFAULT;
    onError(error);
  }

  const tabs = getTabString(tabLevel);

  const displayString = schema.title ? `${schema.title}: ${text}\n` : `${text}`;

  return `${tabs}${displayString}`;
}

/**
 * Handles basic validation and schema parsing before returning
 * a string of copyable ADR data
 * @param {*} schema - usually the display_schema object
 * @param {*} data - usually either `body` or `data` objects found on same level as display_schema
 * @param {Boolean} shouldDisplayBlankFields - Set default value if no data
 * @returns string of data thats displayed in ellington
 */
export const parseADRtoCopy = (
  schema,
  data,
  shouldDisplayBlankFields = false,
  context = {},
) => {
  if (!isValidSchemaType(schema) || !data) {
    return '';
  }

  const validated = validatedPayload({ data: data, display_schema: schema });
  const displaySchema = parseSchema(validated.display_schema, validated.data);

  return walkSchema(
    displaySchema,
    [],
    validated.data,
    shouldDisplayBlankFields,
    undefined,
    undefined,
    undefined,
    { ...context, displaySchema: context.displaySchema ?? displaySchema },
  ).trim();
};

/**
 * A version of parseADRToCopy for a subset of the display schema for object and
 * array types. This formats the object's schema to mimic a top level display
 * schema in order to get the schema's title and indenting of property values
 * @param {*} schema - usually the display_schema object
 * @param {*} data - usually either `body` or `data` objects found on same level as display_schema
 * @param {String[]} - the current json path
 * @param {Boolean} shouldDisplayBlankFields - Set default value if no data
 * @returns string of data thats displayed in ellington
 */
export const parseTypeArrayOrObjectADRToCopy = (
  schema,
  data,
  shouldDisplayBlankFields = false,
  path = [''],
  context = {},
) => {
  if (!isValidSchemaType(schema) || !data || !schema?.title) {
    return '';
  }

  const currentPath = [path[path.length - 1]];

  const updatedSchema = {
    title: schema.title,
    $schema: 'http://json-schema.org/draft-07/schema#',
    type: OBJECT,
    propertiesOrder: currentPath,
    properties: {
      [currentPath]: {
        ...schema,
      },
    },
  };

  const updatedData = {
    // the value is based on if the schema.type
    [currentPath]: schema.type === OBJECT ? { ...data } : [...data],
  };

  const validated = validatedPayload({
    data: updatedData,
    display_schema: updatedSchema,
  });
  const displaySchema = parseSchema(validated.display_schema, validated.data);

  return walkSchema(
    displaySchema,
    [],
    validated.data,
    shouldDisplayBlankFields,
    undefined,
    undefined,
    undefined,
    { ...context, displaySchema: context.displaySchema ?? displaySchema },
  ).trim();
};
